import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
} from "react"
import { isBrowser } from "../utils/environment"

const DeviceContext = createContext()

// Move user agent detection outside component to avoid re-computation
const isMobileBrowser =
  isBrowser() &&
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent,
  )

const getDeviceStates = width => {
  const mobile = width < 840 || isMobileBrowser
  const tablet = width >= 840 && width < 1080 && !isMobileBrowser
  const laptop = width >= 1080 && width < 1440 && !isMobileBrowser
  const desktop = width >= 1440 && !isMobileBrowser

  return { mobile, tablet, laptop, desktop }
}

export const DeviceContextProvider = ({ children }) => {
  const [windowWidth, setWindowWidth] = useState(
    isBrowser() ? window.innerWidth : 1080, // Default to laptop breakpoint for SSR
  )

  // Memoize device states based on window width
  const deviceStates = useMemo(
    () => getDeviceStates(windowWidth),
    [windowWidth],
  )

  useEffect(() => {
    if (!isBrowser()) return

    let timeoutId
    const checkScreenSize = () => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(() => {
        setWindowWidth(window.innerWidth)
      }, 16) // Throttle to ~60fps
    }

    // Set initial value
    checkScreenSize()

    window.addEventListener("resize", checkScreenSize)
    return () => {
      clearTimeout(timeoutId)
      window.removeEventListener("resize", checkScreenSize)
    }
  }, [])

  return (
    <DeviceContext.Provider value={deviceStates}>
      {children}
    </DeviceContext.Provider>
  )
}

export const useDeviceContext = () => {
  const context = useContext(DeviceContext)
  if (!context) {
    throw new Error(
      "useDeviceContext must be used within DeviceContextProvider",
    )
  }
  return context
}
